import React, { useRef } from 'react'
import styles from './FormPostMedia.module.scss'
import camIcon from './../../../../../assets/svg/camera.svg'

import { toast } from 'react-toastify'
import useModal from '../../../../../infrastructure/hooks/useModal'
import { FormLabel } from '../../../../elements/form-label'
import { AppButton } from '../../../../elements/app-button'
import Camera from '../../../../elements/camera/Camera'
import { SELECTED_MEDIA } from '../../../../../infrastructure/dtos/types/file'
import { Controller, useFormContext } from 'react-hook-form'
import { GuestEventFormType } from '../../useGuestEvent'
import { MediaCarousel } from '../../../../elements/media-carousel'
import classNames from 'classnames'
const MAX_MEDIA_COUNT = 10;
const MAX_VIDEO_LENGTH = 10;

const getVideoDimension = (url: string) => {
  return new Promise<{width: number; height: number}>((resove, reject) => {
    const video = document.createElement('video');

      video.onloadedmetadata = () => {
        resove({
          width: video.videoWidth,
          height: video.videoHeight
        })
      };
      video.src = url;
  })
}

const getImageDimension = (url: string) => {
  return new Promise<{width: number; height: number}>((resove, reject) => {
    const image = new Image();

      image.onload = () => {
        resove({
          width: image.width,
          height: image.height
        })
      };
      image.src = url;
  })
}

export function FormPostMedia(props: {onSubmit?: () => Promise<void>; savingPost?: boolean}) {
  const camModal = useModal();
  const {control, watch, setValue, getValues} = useFormContext<GuestEventFormType>()
  const selected = watch('medias') as SELECTED_MEDIA[]
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAtLeastOne = () => {
    const { feeling, medias } = getValues();
    return !!feeling || (medias?.length ?? 0) > 0 || "Please share your thoughts or attach a media at least.";
  };


  const clearFiles = () => {
    for (let index = 0; index < selected.length; index++) {
      const file = selected[index];
      window.URL.revokeObjectURL(file.src);
    }
    setValue('medias', [])
  }

  const getVideoDuration = (url: string): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => resolve(video.duration);
      video.src = url;
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    clearFiles();
    const newFiles = Array.from(files);
    const selectedTemp: SELECTED_MEDIA[] = [];
    
    let countLimitReached = false;
    let videoTooLong = false;

    for (const file of newFiles) {
      const isImage = file.type.startsWith('image');
      const isVideo = file.type.startsWith('video');

      if (!isImage && !isVideo) continue;

      // 1. Check Max Count
      if (selectedTemp.length >= MAX_MEDIA_COUNT) {
        countLimitReached = true;
        continue; 
      }

      const previewUrl = URL.createObjectURL(file);

      try {
        let duration = 0;
        let resolution = { width: 0, height: 0 };

        // 2. Type-Specific Validation
        if (isImage) {
          resolution = await getImageDimension(previewUrl);
        } else {
          duration = await getVideoDuration(previewUrl);
          // Validate Video Length
          if (duration > MAX_VIDEO_LENGTH) {
            videoTooLong = true;
            URL.revokeObjectURL(previewUrl); // Cleanup immediately
            continue;
          }
          resolution = await getVideoDimension(previewUrl);
        }

        // 3. Add Valid Media
        selectedTemp.push({
          src: previewUrl,
          isImage,
          file,
          width: resolution.width,
          quality: resolution.width / (resolution.height || 1),
        });

      } catch (error) {
        console.error("Error processing file:", file.name, error);
        URL.revokeObjectURL(previewUrl);
      }
    }

    // 4. User Feedback
    if (countLimitReached) {
      toast(`Not all files were uploaded. You can upload a maximum of 10 items at a time.`, {
        type: 'warning'
      })
    }
    if (videoTooLong) {
      if(selectedTemp.length > 1) {
        toast(`Not all videos were uploaded. Some videos were skipped because they exceed ${MAX_VIDEO_LENGTH}s.`, {
          type: 'warning'
        })
      } else {
        toast(`Video length should not exceed ${MAX_VIDEO_LENGTH} seconds.`, {
          type: 'warning'
        })
      }
    }

    setValue('medias', selectedTemp);
  };
  console.log('selected', selected)
  const handleButtonClick = () => {
    // Programmatically open the file picker
    fileInputRef.current?.click?.();
  };



  return (
    <div className={styles.container}>
      <Controller
        name="medias"
        control={control}
        rules={{ validate: validateAtLeastOne }}
        render={({ field, fieldState:{error} }) => (
          <>
          
            <div className={styles.labelContainer}>
              <FormLabel>Bring Your Post to Life</FormLabel>
            </div>
            <label className={styles.note}>You can add up to ten photos or videos at a time.</label>
            <div className={styles.cameraContainer}>
                {/* {!windowDimension?.isMobile && <img width={55} onClick={camModal.toogle} src={camIcon as never} alt='cam' />} */}
                <img width={35} onClick={handleButtonClick} src={camIcon as never} alt='cam' />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  accept="video/mp4,video/x-m4v,video/*,image/*,.mp4,.m4v,.mov"
                  style={{ display: 'none' }} // Hide the default input
                />
            </div>
            <span className={styles.warnMessage}>{error?.message}</span>
            {camModal.isOpen && <Camera onMediaSet={ee => {
              field.onChange(ee)
              camModal.toogle()
            }} open={camModal.isOpen} close={camModal.toogle}/>}
          </>
          )}
      />

        {!!selected.length && <MediaCarousel selectedMedia={selected} clearFiles={clearFiles}/>}


      <Controller
        name="feeling"
        control={control}
        rules={{ validate: validateAtLeastOne }}
        render={({ field, fieldState:{error} }) => (
          <div className={styles.feelingContainer}>
            <FormLabel>Tell Us What You're Feeling</FormLabel>
            <textarea className={classNames(styles.textarea, {
              [styles.inputError]: !!error?.message
            })} {...field} rows={6} placeholder='Share your thoughts!'></textarea>
            <span>{error?.message}</span>
          </div>
        )}
      />
      

      <AppButton title='Post' loading={props.savingPost} disabled={props.savingPost} onClick={props.onSubmit} />
    </div>
  )
}
